// Plan 17-07 Task 2 — Splash re-skin (POLISH-13).
// Folk-art Phase 3 — per-screen parity refinement vs the prototype
// `SplashScreen.jsx` (warm-paper sky + dot-grain + floating folk confetti
// behind a floating folk-tile motif, an Ember wordmark with crown + 3D
// drop-shadow, ember/gold 3D CTAs, and a folk sign-in disclosure).
//
// Wordmark bug fix: the full "Runegambit" wordmark is far longer than the
// prototype's "RUNA" and clipped both edges of the 402px frame at the fixed
// 100px size. It now uses a fluid clamp() (token min/max, vw middle) + an em-
// scaled crown so it always fits the viewport while keeping the Ember colour,
// warm 3D ember drop-shadow, and Fredoka display face.
//
// Auth + tutorial routing preserved verbatim (start-tutorial / skip-tutorial
// testids, login section aria, every i18n id). All decorative additions are
// aria-hidden; their motion rides the global reduced-motion caps + the
// `.splash-float` gate in pieces.css.

import { useStore } from '@nanostores/react'
import { type JSX, useEffect, useState } from 'react'
import { FormattedMessage, useIntl } from 'react-intl'

import { useAuth } from '../../auth/AuthProvider.js'
import { useDocumentTitle } from '../../i18n/useDocumentTitle.js'
import { useRouter } from '../../router/router.js'
import { $tutorial } from '../../stores/tutorial.js'
import { Btn } from '../components/Btn.js'
import { BotPracticeCta } from '../lobby/BotPracticeCta.js'
import { BotTierModal } from '../lobby/BotTierModal.js'
import { LoginButtons } from './LoginButtons.js'

// Folk confetti dots (prototype FolkConfettiBg) — warm accent specks drifting
// behind the hero. Position/size/colour mirror the prototype; colours map onto
// the folk accent + house tokens.
const CONFETTI: ReadonlyArray<{
  top: string
  left: string
  color: string
  size: number
}> = [
  { top: '12%', left: '14%', color: 'var(--color-accent-gold)', size: 14 },
  { top: '20%', left: '82%', color: 'var(--color-player-b)', size: 10 },
  { top: '68%', left: '8%', color: 'var(--color-accent-plum)', size: 11 },
  { top: '78%', left: '86%', color: 'var(--color-accent-leaf)', size: 13 },
  { top: '42%', left: '90%', color: 'var(--color-player-a)', size: 9 },
  { top: '56%', left: '6%', color: 'var(--color-accent-grape)', size: 10 },
]

// Floating folk-tile motif (prototype BoardMark) — RUNE letter tiles in the
// rarity palette, jaunty static rotations on a gently floating row.
const MOTIF_TILES: ReadonlyArray<{
  ch: string
  bg: string
  ink: string
  rotate: string
}> = [
  {
    ch: 'R',
    bg: 'var(--color-rarity-common-bg)',
    ink: 'var(--color-rarity-common-ink)',
    rotate: 'rotate(-7deg)',
  },
  {
    ch: 'U',
    bg: 'var(--color-rarity-uncommon-bg)',
    ink: 'var(--color-rarity-uncommon-ink)',
    rotate: 'rotate(4deg)',
  },
  {
    ch: 'N',
    bg: 'var(--color-rarity-common-bg)',
    ink: 'var(--color-rarity-common-ink)',
    rotate: 'rotate(-3deg)',
  },
  {
    ch: 'E',
    bg: 'var(--color-rarity-royal-bg)',
    ink: 'var(--color-rarity-royal-ink)',
    rotate: 'rotate(8deg)',
  },
]

/** Warm folk dot-grain + floating confetti specks behind the hero. */
function SplashBackdrop(): JSX.Element {
  return (
    <div
      aria-hidden="true"
      style={{
        position: 'absolute',
        inset: 0,
        zIndex: 0,
        pointerEvents: 'none',
        overflow: 'hidden',
        // Painted folk dot-grain (prototype .runa-root::before).
        backgroundImage:
          'radial-gradient(color-mix(in srgb, var(--color-ink-faint) 16%, transparent) 1px, transparent 1.5px)',
        backgroundSize: '16px 16px',
        opacity: 0.5,
      }}
    >
      {CONFETTI.map((d, i) => (
        <span
          key={`${d.top}-${d.left}`}
          className="splash-float"
          style={{
            position: 'absolute',
            top: d.top,
            left: d.left,
            width: `${d.size}px`,
            height: `${d.size}px`,
            borderRadius: 'var(--radius-pill)',
            background: d.color,
            opacity: 0.5,
            animationDelay: `${i * 0.4}s`,
          }}
        />
      ))}
    </div>
  )
}

/** Little folk crown perched over the wordmark (prototype Wordmark crown). */
function WordmarkCrown(): JSX.Element {
  return (
    <svg aria-hidden="true" className="wordmark-crown" viewBox="0 0 60 40">
      <path
        d="M6 36 L2 10 L18 24 L30 4 L42 24 L58 10 L54 36 Z"
        fill="var(--color-accent-gold)"
        stroke="var(--color-accent-gold-deep)"
        strokeWidth="2.5"
        strokeLinejoin="round"
      />
      <circle cx="30" cy="4" r="4" fill="var(--color-player-a)" />
      <circle cx="2" cy="10" r="3.5" fill="var(--color-accent-plum)" />
      <circle cx="58" cy="10" r="3.5" fill="var(--color-player-b)" />
    </svg>
  )
}

/** Floating folk RUNE tile motif above the wordmark. */
function SplashMotif(): JSX.Element {
  return (
    <div
      aria-hidden="true"
      style={{
        display: 'flex',
        justifyContent: 'center',
        gap: 'var(--space-2)',
        marginBottom: 'var(--space-3)',
      }}
    >
      {MOTIF_TILES.map((tile, i) => (
        // Outer wrapper keeps the static jaunty rotation; the inner span owns
        // the float so the `splashFloat` keyframe's translateY transform does
        // not clobber the per-tile rotate.
        <span key={tile.ch} style={{ transform: tile.rotate }}>
          <span
            // Each tile floats on its own staggered cadence (prototype
            // BoardMark `floaty ... ${i*0.15}s`) instead of one uniform row
            // bob. The `.splash-float` rule (pieces.css) owns the keyframe +
            // the dual reduced-motion gate (prefers-reduced-motion +
            // [data-motion="reduced"]); we only desync the per-tile phase via
            // inline animation-delay, so the gate (animation:none) still fully
            // suppresses it.
            className="splash-float"
            style={{
              display: 'grid',
              placeItems: 'center',
              width: '44px',
              height: '44px',
              borderRadius: 'var(--radius-tile)',
              background: tile.bg,
              color: tile.ink,
              boxShadow: 'var(--shadow-tile)',
              fontFamily: 'var(--font-family-display)',
              fontWeight: 700,
              fontSize: 'var(--font-size-22)',
              animationDelay: `${i * 0.15}s`,
            }}
          >
            {tile.ch}
          </span>
        </span>
      ))}
    </div>
  )
}

/** Play glyph for the primary splash CTA (tutorial for new players, lobby for returning). */
function PlayGlyph(): JSX.Element {
  return (
    <svg aria-hidden="true" width={18} height={18} viewBox="0 0 24 24">
      <path d="M8 5v14l11-7z" fill="var(--color-on-action)" />
    </svg>
  )
}

export function SplashScreen(): JSX.Element {
  useDocumentTitle('doc.title.splash')
  const intl = useIntl()
  const auth = useAuth()
  const router = useRouter()
  const tutorial = useStore($tutorial)
  const [showLogin, setShowLogin] = useState(false)
  const [botTierOpen, setBotTierOpen] = useState(false)
  const isReturning = tutorial.completed || tutorial.skipped
  const isAuthenticated = auth.kind === 'authenticated'

  // Returning + authenticated player: bypass splash entirely. Run as a
  // mount-time effect so the redirect happens after first paint.
  useEffect(() => {
    if (isAuthenticated && tutorial.completed) {
      router.navigate('/lobby')
    }
  }, [isAuthenticated, tutorial.completed, router])

  function startTutorial(): void {
    router.navigate('/tutorial')
  }

  function skipToLobby(): void {
    router.navigate('/lobby')
  }

  return (
    <main
      id="main"
      tabIndex={-1}
      className="rg-screen splash"
      data-screen="splash"
      style={{
        position: 'relative',
        minHeight: '100vh',
        overflow: 'hidden',
        // Warm folk sky radial (prototype .runa-root background).
        background:
          'radial-gradient(120% 80% at 50% -10%, var(--color-sky-1) 0%, var(--color-sky-2) 46%, var(--color-paper) 100%)',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        padding: 'var(--space-5)',
        gap: 'var(--space-5)',
      }}
    >
      <SplashBackdrop />

      <header
        className="splash-hero"
        style={{
          position: 'relative',
          zIndex: 1,
          textAlign: 'center',
          color: 'var(--color-text-primary)',
        }}
      >
        <SplashMotif />
        <div
          style={{
            position: 'relative',
            display: 'inline-block',
            maxWidth: '100%',
            // Fluid wordmark scale: clamp between display tokens with a vw
            // middle so "Runegambit" never overruns the viewport. The em-sized
            // crown reads this as its font context.
            fontSize: 'clamp(var(--font-size-36), 13vw, var(--font-size-100))',
          }}
        >
          <WordmarkCrown />
          <h1
            className="display-word"
            style={{
              margin: 0,
              fontSize: '1em',
              maxWidth: '100%',
              whiteSpace: 'nowrap',
              color: 'var(--color-player-a)',
              // Warm 3D ember drop (prototype Wordmark textShadow).
              textShadow:
                '0 3px 0 var(--color-player-a-deep), 0 4px 8px color-mix(in srgb, var(--color-player-a-deep) 30%, transparent)',
            }}
          >
            <FormattedMessage id="splash.title" defaultMessage="Runegambit" />
          </h1>
        </div>
        <p
          className="splash-subtitle"
          style={{
            fontFamily: 'var(--font-family-display)',
            fontWeight: 500,
            fontSize: 'var(--font-size-18)',
            margin: 'var(--space-3) 0 0',
            color: 'var(--color-text-secondary)',
          }}
        >
          <FormattedMessage
            id="splash.subtitle"
            defaultMessage="A word-and-board game of runes."
          />
        </p>
      </header>

      <section
        className="splash-cta"
        style={{
          position: 'relative',
          zIndex: 1,
          display: 'flex',
          flexDirection: 'column',
          gap: 'var(--space-3)',
          width: '100%',
          maxWidth: '320px',
        }}
      >
        {/* ARCA-132 — the splash primary tracks the player's actual next step.
            A first-time player begins the tutorial; a returning player (who's
            already completed/skipped it) just wants to play, so the lobby
            becomes the ember CTA and the tutorial demotes to a quiet ghost
            "Replay". testIds track the ACTION, not the visual treatment, so
            `start-tutorial` always triggers startTutorial and `skip-tutorial`
            always triggers skipToLobby (kept stable for SplashScreen.test.tsx
            + the onboarding e2e specs). */}
        {isReturning ? (
          <>
            <Btn
              kind="primary"
              size="lg"
              block
              onClick={skipToLobby}
              testId="skip-tutorial"
            >
              <PlayGlyph />
              <FormattedMessage
                id="onboarding.cta.returning_primary"
                defaultMessage="Play"
              />
            </Btn>
            <Btn
              kind="ghost"
              size="md"
              block
              onClick={startTutorial}
              testId="start-tutorial"
            >
              <FormattedMessage
                id="onboarding.cta.replay_tutorial"
                defaultMessage="Replay tutorial"
              />
            </Btn>
          </>
        ) : (
          <Btn
            kind="primary"
            size="lg"
            block
            onClick={startTutorial}
            testId="start-tutorial"
          >
            <PlayGlyph />
            <FormattedMessage
              id="onboarding.cta.start_tutorial"
              defaultMessage="Begin the tutorial"
            />
          </Btn>
        )}

        {/* ARCA-93 / WS2 — sign-in-free "Practice vs the bot" launcher. Opens
            the same bot-tier flow as the lobby; the modal mints an anon
            session on submit (BotTierModal → ensureAnonSession), so a guest
            starts and finishes a bot match with zero sign-in. Hidden for an
            authenticated player, who is sent to /lobby and uses the lobby's
            own bot-practice entry. */}
        {!isAuthenticated && (
          <BotPracticeCta onClick={() => setBotTierOpen(true)} />
        )}
      </section>

      <section
        className="splash-login"
        aria-label={intl.formatMessage({ id: 'splash.login.section_aria' })}
        style={{
          position: 'relative',
          zIndex: 1,
          width: '100%',
          maxWidth: '320px',
        }}
      >
        <Btn
          kind="them"
          size="sm"
          onClick={() => setShowLogin((s) => !s)}
          pressed={showLogin}
        >
          <FormattedMessage
            id="splash.login.disclose"
            defaultMessage="Save your progress?"
          />
          <span
            aria-hidden="true"
            style={{
              display: 'inline-block',
              transform: showLogin ? 'rotate(180deg)' : 'none',
              transition: 'transform var(--duration-fast) var(--ease-out)',
            }}
          >
            ▾
          </span>
        </Btn>
        {showLogin && (
          // Sign-in disclosure reveal (prototype SplashScreen sign-in
          // `bounceInUp .4s var(--ease-bounce)`). The `.splash-signin-reveal`
          // rule (pieces.css) owns the keyframe + the dual reduced-motion gate
          // (prefers-reduced-motion + [data-motion="reduced"]); under reduced
          // motion the block simply appears with no entrance animation.
          <div
            className="splash-signin-reveal"
            style={{ marginTop: 'var(--space-2)' }}
          >
            <LoginButtons />
          </div>
        )}
      </section>

      <BotTierModal open={botTierOpen} onClose={() => setBotTierOpen(false)} />
    </main>
  )
}
